import React from 'react';
import { TUTORIAL_DESIGNS, TUTORIAL_TOTAL_MINUTES } from '../../../tutorials';
import { useTutorial } from '../TutorialProvider';
import './TutorialDesignPicker.css';

/**
 * Practice-design select for the guided tutorial.
 *
 * Replaces the "go upload something" step: the learner picks a design and it
 * is loaded for them, so the walkthrough always runs on artwork whose steps
 * have been authored.
 *
 * Lessons already finished are marked rather than hidden — a replay is a
 * legitimate thing to want, and a roster that shrinks as you work through it
 * loses the sense of progress.
 */
const TutorialDesignPicker = ({ onSelect, busyId }) => {
  const { hasCompletedLesson } = useTutorial();

  // The three lessons together are the product's whole workflow, so the goal is
  // all of them rather than any one. Quoting what's actually left — rather than
  // the same total every time — keeps that goal in reach for someone coming
  // back for their second, and stops it reading as a fresh 20-minute ask.
  const left = TUTORIAL_DESIGNS.filter((design) => !hasCompletedLesson(design.tutorialId));
  const minutesLeft = left.reduce((total, design) => total + (design.minutes || 0), 0);

  let remaining;
  if (!left.length) {
    remaining = 'You\'ve done all three — replay any of them whenever you like.';
  } else if (left.length === TUTORIAL_DESIGNS.length) {
    remaining = `All ${TUTORIAL_DESIGNS.length} lessons take about ${TUTORIAL_TOTAL_MINUTES} minutes together, and cover the whole workflow end to end.`;
  } else {
    remaining = `${left.length} lesson${left.length > 1 ? 's' : ''} to go — about ${minutesLeft} minutes to finish the set.`;
  }

  return (
  <div className="tutorial-design-picker">
    <div className="tutorial-design-grid">
      {TUTORIAL_DESIGNS.map((design, index) => {
        const busy = busyId === design.id;
        const done = hasCompletedLesson(design.tutorialId);

        return (
          // The badge is a link, so it can't live inside the card's button —
          // it sits over it instead, in a wrapper the card fills.
          <div
            key={design.id}
            className="tutorial-design-slot"
            style={{ animationDelay: `${index * 90}ms` }}
          >
            <button
              type="button"
              className={`tutorial-design-card ${busy ? 'is-busy' : ''} ${done ? 'is-done' : ''}`}
              onClick={() => !busyId && onSelect(design)}
              disabled={!!busyId}
            >
              {/* Both live in the notch along the top edge, and only one of
                  them can: they're the same width and neither reads at half.
                  Difficulty is what you weigh before starting, so once the
                  lesson is behind you the ✓ takes the slot. */}
              {done ? (
                <span className="tutorial-design-done">✓ Completed</span>
              ) : (
                <span className="tutorial-design-level">{design.level}</span>
              )}

              {design.minutes && (
                <span className="tutorial-design-time">{design.minutes} min</span>
              )}

              <span className="tutorial-design-art">
                <img src={design.image} alt="" />
              </span>

              <span className="tutorial-design-name">{design.name}</span>
              <span className="tutorial-design-blurb">{design.blurb}</span>

              <span className="tutorial-design-cta">
                {busy ? 'Loading…' : done ? 'Do it again ▶' : 'Start with this ▶'}
              </span>
            </button>

            {design.instagram && (
              <a
                className="tutorial-design-insta"
                href={design.instagram}
                target="_blank"
                rel="noreferrer"
                title="As seen on Instagram"
              >
                <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
                  <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" fill="none" stroke="currentColor" strokeWidth="2.4" />
                  <circle cx="12" cy="12" r="4.6" fill="none" stroke="currentColor" strokeWidth="2.4" />
                  <circle cx="17.6" cy="6.4" r="1.5" fill="currentColor" />
                </svg>
                As seen on Instagram
              </a>
            )}
          </div>
        );
      })}
    </div>

    <p className="tutorial-design-total">{remaining}</p>
  </div>
  );
};

export default TutorialDesignPicker;
